import { useState } from "react";
import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import { map, skip } from "rxjs";
import { useQueryParams$ } from "../../common/hooks/useQueryParams$";
import {
  getAttributeList,
  getDateAttribute,
} from "../../common/searchParams.utils";
import { AdminReportSearch, REPORT_TYPE_OPTION } from "./AdminReportSearch";
import { AdminReportTable } from "./AdminReportTable";
import { HTTP_METHODS } from "../../utils/constants";
import { ModalLengthZero } from "./ModalLengthZero/ModalLengthZero";
import { networkBackendRequest } from "../../utils/networkUtils";
import {
  ReportConvertResponseAppoinment,
  ReportConvertResponseTraceability,
  ReportConvertResponseVolante,
} from "./ReportConvertResponse";
import { getErrorValueResponse } from "../../common/error/Error";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { removeNullUndefinedProps } from "../../common/utils";
import { Authorization } from "../../common/AuthorizationCpt";
import { ReportAdminParams } from "./interfaces/Report.interfaces";
import {
  CITATION_REPORT,
  FLYERS_REPORT,
  YYYY_MM_DD,
} from "../../utils/valueStaticConstants";
import {
  REPORT_CREATE_APPOINTMENTS_REPORT,
  REPORT_CREATE_VOLANTES_REPORT,
  REPORT_GET_APPOINTMENTS_REPORT,
  REPORT_GET_REPORTS,
  REPORT_GET_VOLANTES_REPORT,
} from "../../utils/permissionConstant";

export enum StatusType {
  FAILED = "FAILED",
  NULL = "NULL",
  PROCESSING = "PROCESSING",
  SUCCESS = "SUCCESS",
}

export const AdminReportResource = () => {
  const params$ = useQueryParams$().pipe(
    map((params: ReportAdminParams) => {
      const reportType = params.reportType || FLYERS_REPORT;
      const year_dam = params.year_dam || new Date().getFullYear().toString();
      const regimen_dam = params.regimen_dam || "10";
      const volante_nums = getAttributeList(params.volante_nums);
      const id_pedidos = getAttributeList(params.id_pedidos);
      const tci_nums = getAttributeList(params.tci_nums);
      return {
        ...params,
        reportType,
        year_dam,
        regimen_dam,
        volante_nums,
        id_pedidos,
        tci_nums,
      };
    })
  );

  const [reportTypeName, setReportTypeName] = useState(FLYERS_REPORT);
  const [statusEndpoint, setStatusEndpoint] = useState<StatusType>(
    StatusType.NULL
  );
  const [countEqualToZero, setCountEqualToZero] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string>("");

  const loadReportExecutor = useAsyncExecutor2(
    (params: any) => {
      const selectedOption = REPORT_TYPE_OPTION.find(
        (option) => option.value === params.reportType
      );

      const url = selectedOption.endpoint;

      const convertResponseDictionary = {
        flyers_report: ReportConvertResponseVolante,
        citation_report: ReportConvertResponseAppoinment,
        tracking_report: ReportConvertResponseTraceability,
      };

      const paramsVolanteAppointment = {
        order: "descending",
        page: params.page,
        page_size: params.page_size,
        agent_ruc: params.agent_ruc,
        dam_num:
          params.num_dam &&
          params.num_dam.length === 6 &&
          `235-${params.year_dam}-${params.regimen_dam}-${params.num_dam}`,
        volante_nums: params.volante_nums,
        from_date: params.from_date,
        to_date: params.to_date,
        tci_nums: params.tci_nums,
      };
      if (params.reportType === FLYERS_REPORT) {
        const paramsVolante = {
          ...paramsVolanteAppointment,
          id_pedidos: params.id_pedidos,
        };
        const cleanedParamsVolante = removeNullUndefinedProps(paramsVolante);
        return networkBackendRequest(
          url,
          HTTP_METHODS.GET,
          cleanedParamsVolante
        )
          .then((response) => response.json())
          .then(convertResponseDictionary[params.reportType]);
      }

      if (params.reportType === CITATION_REPORT) {
        const cleanedParamsAppointment = removeNullUndefinedProps(
          paramsVolanteAppointment
        );
        return networkBackendRequest(
          url,
          HTTP_METHODS.GET,
          cleanedParamsAppointment
        )
          .then((response) => response.json())
          .then(convertResponseDictionary[params.reportType]);
      }
    },

    {
      params$: params$.pipe(
        skip(1),
        map((params) => {
          const from_date = getDateAttribute(params.from_date);
          const to_date = getDateAttribute(params.to_date);
          return {
            ...params,
            reportType: params.reportType,
            from_date: format(from_date, YYYY_MM_DD),
            to_date: format(to_date, YYYY_MM_DD),
            order: "descending",
            page: params.page ? params.page : 1,
            page_size: 8,
          };
        })
      ),
      onSuccess: (data, params) => {
        const { reportType } = params;
        const { count } = data;
        setReportTypeName(reportType);
        setStatusEndpoint(StatusType.SUCCESS);
        if (count === 0) {
          setCountEqualToZero(true);
        }
      },
      onError: (error) => {
        const errorMessage = getErrorValueResponse(error);
        setShowErrorMessage(errorMessage);
      },
    }
  );

  const reportChangeState = (report: string) => {
    setReportTypeName(report);
    setStatusEndpoint(StatusType.NULL);
  };
  return (
    <div>
      <Authorization
        actions={[
          REPORT_GET_VOLANTES_REPORT,
          REPORT_CREATE_VOLANTES_REPORT,
          REPORT_GET_APPOINTMENTS_REPORT,
          REPORT_CREATE_APPOINTMENTS_REPORT,
          REPORT_GET_REPORTS,
        ]}
      >
        <AdminReportSearch
          params$={params$}
          reportChangeState={reportChangeState}
          loadReportExecutor={loadReportExecutor}
          statusEndpoint={statusEndpoint}
        />
        <AdminReportTable
          loadReportExecutor={loadReportExecutor}
          reportTypeName={reportTypeName}
          statusEndpoint={statusEndpoint}
        />
        <ModalLengthZero
          showModalLengthZero={countEqualToZero}
          onAccept={() => {
            setCountEqualToZero(false);
          }}
        />
        {showErrorMessage && (
          <ConfirmationModal
            message={showErrorMessage}
            onAccept={() => {
              setShowErrorMessage("");
            }}
            title={`Atención`}
            toggleModal={() => {
              setShowErrorMessage("");
            }}
          />
        )}
      </Authorization>
    </div>
  );
};
